import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const TOC = [
  { id: 'cancelamento',     label: '1. Cancelamento de assinatura' },
  { id: 'garantia',         label: '2. Garantia de 7 dias (Direito de Arrependimento)' },
  { id: 'nao-reembolsavel', label: '3. Conteúdo digital não reembolsável' },
  { id: 'creditos',         label: '4. Créditos avulsos' },
  { id: 'falha',            label: '5. Falha no pagamento' },
  { id: 'contato',          label: '6. Contato' },
];

export function Cancellation() {
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [dbHtml,      setDbHtml]      = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('legal_documents')
      .select('content_html, published_at')
      .eq('doc_type', 'cancellation')
      .eq('is_current', true)
      .maybeSingle()
      .then(({ data }) => {
        if (data) { setPublishedAt(data.published_at); setDbHtml(data.content_html); }
      });
  }, []);

  const lastUpdated = publishedAt
    ? new Date(publishedAt).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
    : '22 de abril de 2026';

  return (
    <>
      <Helmet>
        <title>Cancelamento e Reembolso, Professya</title>
        <meta name="description" content="Regras de cancelamento e reembolso do Professya: garantia de 7 dias, conteúdo digital, créditos avulsos e mais." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)', padding: '2rem 1.5rem' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>

          <Link to="/" style={{ color: 'var(--petrol)', textDecoration: 'none', fontSize: 'var(--text-label)', display: 'inline-block', marginBottom: '2rem' }}>
            ← Voltar ao início
          </Link>

          <h1 className="font-display" style={{ fontSize: 'clamp(1.5rem,4vw,2.25rem)', marginBottom: '0.5rem' }}>
            Cancelamento e Reembolso
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', marginBottom: '2rem' }}>
            Última atualização: {lastUpdated}
          </p>

          {dbHtml ? (
            <div dangerouslySetInnerHTML={{ __html: dbHtml }} />
          ) : (<>

          {/* ToC */}
          <div className="card" style={{ marginBottom: '2rem' }}>
            <p style={{ fontWeight: 600, marginBottom: '0.75rem' }}>Índice</p>
            <ol style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 2 }}>
              {TOC.map(s => (
                <li key={s.id}>
                  <a href={`#${s.id}`} style={{ color: 'var(--petrol)', fontSize: 'var(--text-label)' }}>{s.label}</a>
                </li>
              ))}
            </ol>
          </div>

          <Sec id="cancelamento" title="1. Cancelamento de assinatura">
            <p>Você pode cancelar sua assinatura a qualquer momento, sem multa ou taxa:</p>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li>No aplicativo: <strong>Perfil → Assinatura → Cancelar</strong></li>
              <li>No iOS: Ajustes → [seu nome] → Assinaturas → Professya → Cancelar</li>
              <li>No Android: Google Play → Assinaturas → Professya → Cancelar</li>
              <li>Por e-mail: <strong>suporte@professya.com</strong></li>
            </ul>
            <p style={{ marginTop: '0.75rem' }}>
              O cancelamento entra em vigor no final do período já pago. Você mantém acesso completo até a data de vencimento.
            </p>
          </Sec>

          <Sec id="garantia" title="2. Garantia de 7 dias (Direito de Arrependimento)">
            <p>
              Em conformidade com o <strong>Art. 49 do Código de Defesa do Consumidor (CDC)</strong>, você tem
              o direito de cancelar sua assinatura em até <strong>7 dias corridos</strong> a partir da data da
              compra, com reembolso integral do valor pago, desde que nenhum serviço de geração de conteúdo
              personalizado tenha sido utilizado no período.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              Se serviços foram utilizados dentro dos 7 dias, o reembolso será calculado descontando o valor
              de referência dos serviços consumidos (conforme tabela de preços avulsos vigente na data do uso).
              Se o valor dos serviços consumidos for igual ou superior ao valor da assinatura, não haverá reembolso.
            </p>
            <p style={{ marginTop: '0.75rem' }}>Como solicitar:</p>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li>Por e-mail: <strong>suporte@professya.com</strong> com o assunto "Cancelamento CDC Art. 49"</li>
              <li>Pelo aplicativo: Perfil → Assinatura → Cancelar (dentro do prazo de 7 dias)</li>
            </ul>
            <p style={{ marginTop: '0.75rem' }}>
              O reembolso será processado em até <strong>10 dias úteis</strong> pelo mesmo método de pagamento utilizado na compra.
            </p>
          </Sec>

          <Sec id="nao-reembolsavel" title="3. Conteúdo digital não reembolsável">
            <p>
              Determinados serviços envolvem a geração de conteúdo digital personalizado e sob demanda,
              entregue imediatamente no ato da solicitação. Por sua natureza, este conteúdo não pode ser
              "devolvido" após a geração.
            </p>
            <Sub>3.1 Serviços não reembolsáveis</Sub>
            <p style={{ marginBottom: '0.5rem' }}>
              Esta categoria inclui: Mapa Astral, Sinastria, AstroMatch, Tarot de Marselha, Tarot Cigano,
              Numerologia, Rota Profissional, Eclipse Insight, Astrocartografia, Human Design, Astrologia
              Védica, I Ching, Radar Retrógrado, Astro Coach, Previsão Mensal Personalizada, Previsão Anual
              Personalizada e qualquer outro serviço que gere análise personalizada sob demanda.
            </p>
            <Sub>3.2 Consentimento prévio</Sub>
            <p>Antes de gerar qualquer serviço desta categoria, o sistema exibirá a seguinte confirmação:</p>
            <div style={{ backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)', padding: '0.875rem 1rem', margin: '0.75rem 0', fontStyle: 'italic' }}>
              "Estou solicitando a entrega imediata de conteúdo digital personalizado gerado especificamente para mim. Compreendo que, após a geração, este serviço não poderá ser reembolsado, conforme Art. 49, parágrafo único do CDC. Confirmo minha solicitação."
            </div>
            <p>Este consentimento é registrado com data, hora e identificação da conta.</p>
            <Sub>3.3 Serviços recorrentes</Sub>
            <p>
              Previsões diárias, semanais, mensais e anuais são consideradas entregues proporcionalmente ao
              período de uso. Em caso de cancelamento após os 7 dias de arrependimento, não há reembolso do
              período já pago, mas o acesso é mantido até o vencimento.
            </p>
          </Sec>

          <Sec id="creditos" title="4. Créditos avulsos">
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2 }}>
              <li>Créditos são consumidos no momento em que você solicita a geração de leituras avulsas</li>
              <li>Créditos utilizados não são reembolsáveis, exceto em casos de <strong>falha técnica documentada</strong> atribuível ao Professya</li>
              <li>Créditos não possuem data de expiração enquanto sua conta estiver ativa</li>
              <li>Créditos não são transferíveis entre contas</li>
              <li>Em caso de encerramento definitivo do serviço, créditos não utilizados serão reembolsados proporcionalmente, mediante aviso prévio de no mínimo 30 dias</li>
            </ul>
          </Sec>

          <Sec id="falha" title="5. Falha no pagamento">
            <p>
              Em caso de falha no pagamento, o Professya tentará processar a cobrança novamente por até
              <strong> 3 dias consecutivos</strong>. Após esse período sem sucesso, o acesso será reduzido ao
              plano Gratuito. Seus dados e histórico de análises serão mantidos por <strong>30 dias</strong> para
              reativação.
            </p>
          </Sec>

          <Sec id="contato" title="6. Contato">
            <p>Para solicitar cancelamento, reembolso ou esclarecer dúvidas:</p>
            <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, marginTop: '0.5rem' }}>
              <li><strong>E-mail:</strong> suporte@professya.com</li>
              <li><strong>Assunto sugerido:</strong> "Cancelamento" ou "Reembolso, [descreva seu pedido]"</li>
              <li><strong>Prazo de resposta:</strong> até 5 dias úteis</li>
            </ul>
            <p style={{ marginTop: '0.75rem' }}>
              Para questões relacionadas à privacidade e exclusão de dados, consulte a{' '}
              <Link to="/privacy" style={{ color: 'var(--petrol)' }}>Política de Privacidade</Link>.
            </p>
          </Sec>

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '1.5rem', marginTop: '2rem', color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.8 }}>
            <p>Professya, Segredo Astral | professya.com</p>
            <p style={{ marginTop: '0.5rem' }}>
              <Link to="/terms" style={{ color: 'var(--petrol)' }}>Termos de Uso</Link>
              {' · '}
              <Link to="/privacy" style={{ color: 'var(--petrol)' }}>Política de Privacidade</Link>
              {' · '}
              <Link to="/" style={{ color: 'var(--petrol)' }}>Voltar ao início</Link>
            </p>
          </div>

          </>)}
        </div>
      </div>
    </>
  );
}

function Sec({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} style={{ marginBottom: '2rem' }}>
      <h2 className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.875rem', color: 'var(--text-primary)' }}>{title}</h2>
      <div style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>{children}</div>
    </section>
  );
}

function Sub({ children }: { children: React.ReactNode }) {
  return <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '1rem 0 0.35rem' }}>{children}</p>;
}
